import { ReactNode, UIEvent, useCallback, useEffect, useRef, useState } from 'react';
import InboxOutlined from 'assets/svg/InboxOutlined';
import Loader from 'components/molecules/Loader';
import { PPA_WHITESPACE_GRID_EMPTY, PPA_WHITESPACE_GRID_LOADING } from 'constants/ppaTestIds';

export type GenericGridRow<TCell> = {
    bucketLabel: string;
    cells: Record<string, TCell | null | undefined>;
};

type GenericWhitespaceGridProps<TCell> = {
    columnHeaders: string[];
    gridRows: GenericGridRow<TCell>[];
    renderCell: (cell: NonNullable<TCell>, rowLabel: string, columnHeader: string) => ReactNode;
    emptyCellNode?: ReactNode;
    isFetched?: boolean;
    onLoadMoreRows?: () => void | Promise<void>;
    onLoadMoreColumns?: () => void | Promise<void>;
    hasMoreRows?: boolean;
    hasMoreColumns?: boolean;
    isLoadingMoreRows?: boolean;
    isLoadingMoreColumns?: boolean;
    isGridFetching?: boolean;
    maxHeight?: number | null;
    'data-testid'?: string;
};

const DEFAULT_EMPTY_CELL_NODE = (
    <div className="flex h-full min-h-[220px] items-center justify-center bg-[#f0f0f0] text-xs text-[#71717b]">
        {null}
    </div>
);

const CELL_WIDTH = 225;
const DEFAULT_CONTAINER_HEIGHT = 758;
const MIN_CONTAINER_HEIGHT = 320;

const GenericWhitespaceGrid = <TCell,>({
    columnHeaders,
    gridRows,
    renderCell,
    emptyCellNode,
    isFetched,
    onLoadMoreRows,
    onLoadMoreColumns,
    hasMoreRows = false,
    hasMoreColumns = false,
    isLoadingMoreRows = false,
    isLoadingMoreColumns = false,
    isGridFetching,
    maxHeight,
    'data-testid': testId,
}: GenericWhitespaceGridProps<TCell>) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [fillerWidths, setFillerWidths] = useState<number[]>([]);
    const isPaginating = isLoadingMoreRows || isLoadingMoreColumns;
    const normalizedMaxHeight =
        typeof maxHeight === 'number' && !Number.isNaN(maxHeight)
            ? Math.max(maxHeight, MIN_CONTAINER_HEIGHT)
            : undefined;
    const effectiveGridHeight = normalizedMaxHeight ?? DEFAULT_CONTAINER_HEIGHT;

    const handleScroll = useCallback(
        (event: UIEvent<HTMLDivElement>) => {
            const target = event.currentTarget;
            const verticalRemaining = target.scrollHeight - target.scrollTop - target.clientHeight;
            const horizontalRemaining = target.scrollWidth - target.scrollLeft - target.clientWidth;
            const threshold = 80;

            if (verticalRemaining <= threshold && hasMoreRows && !isLoadingMoreRows) {
                onLoadMoreRows?.();
            }

            if (horizontalRemaining <= threshold && hasMoreColumns && !isLoadingMoreColumns) {
                onLoadMoreColumns?.();
            }
        },
        [hasMoreColumns, hasMoreRows, isLoadingMoreColumns, isLoadingMoreRows, onLoadMoreColumns, onLoadMoreRows],
    );

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const computeFillers = () => {
            const containerWidth = el.clientWidth;
            const labelCell = el.querySelector('thead th');
            const labelWidth = labelCell ? labelCell.getBoundingClientRect().width : 0;
            const available = containerWidth - labelWidth;
            const dataWidth = columnHeaders.length * CELL_WIDTH;

            if (dataWidth >= available) {
                setFillerWidths([]);
                return;
            }

            const remaining = available - dataWidth;
            const fullCount = Math.floor(remaining / CELL_WIDTH);
            const partial = remaining - fullCount * CELL_WIDTH;
            const widths: number[] = Array(fullCount).fill(CELL_WIDTH);
            if (partial > 0) widths.push(partial);
            setFillerWidths(widths);
        };

        computeFillers();
        const observer = new ResizeObserver(computeFillers);
        observer.observe(el);
        return () => observer.disconnect();
    }, [columnHeaders.length]);

    if (!isFetched || isGridFetching) {
        return (
            <div
                className="w-full bg-white rounded-lg"
                style={{ height: effectiveGridHeight }}
                data-testid={PPA_WHITESPACE_GRID_LOADING}
            >
                <Loader size="default" />
            </div>
        );
    }

    if (gridRows.length === 0) {
        return (
            <div
                className="flex w-full items-center justify-center rounded-lg border border-[#e4e4e7] bg-[#f8f9fa] px-8"
                style={{ minHeight: effectiveGridHeight }}
                data-testid={PPA_WHITESPACE_GRID_EMPTY}
            >
                <div className="flex flex-col items-center justify-center gap-2 text-center">
                    <InboxOutlined customIndex={0} />
                    <div className="flex flex-col items-center gap-1">
                        <p className="text-[16px] leading-8 font-semibold text-[rgba(0,0,0,0.88)]">No data found</p>
                        <p className="text-[14px] leading-[22px] text-[rgba(0,0,0,0.45)]">
                            No data found for the selected filters
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="relative w-full max-h-[758px] overflow-auto rounded-lg border border-[#e4e4e7]"
            style={{ maxHeight: effectiveGridHeight }}
            onScroll={handleScroll}
            data-testid={testId}
        >
            {isPaginating ? (
                <div className="pointer-events-none absolute inset-0 z-[8] flex items-center justify-center bg-white/30">
                    <Loader size="default" />
                </div>
            ) : null}
            <table className={`border-separate border-spacing-0 ${isPaginating ? 'opacity-50' : ''}`}>
                <thead>
                    <tr className="bg-[#fafafa]">
                        <th className="sticky left-0 top-0 z-[11] whitespace-nowrap border border-[#e4e4e7] bg-[#fafafa] px-2 py-3 text-left text-[14px] font-semibold text-[#3f3f46]">
                            {''}
                        </th>
                        {columnHeaders.map((header) => (
                            <th
                                key={header}
                                className="sticky top-0 z-[10] w-[225px] min-w-[225px] max-w-[225px] border border-[#e4e4e7] bg-[#fafafa] px-3 py-3 text-center text-[14px] font-semibold text-[#3f3f46]"
                            >
                                {header}
                            </th>
                        ))}
                        {fillerWidths.map((w, i) => (
                            <th
                                key={`filler-${i}`}
                                className="sticky top-0 z-20 border border-[#e4e4e7] bg-[#fafafa] py-3"
                                style={{ width: w, minWidth: w, maxWidth: w }}
                            />
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {gridRows.map((row) => (
                        <tr key={row.bucketLabel} className="h-[220px]">
                            <td className="sticky whitespace-nowrap left-0 z-[9] border border-[#e4e4e7] bg-[#fafafa] px-2 py-3 text-center align-middle text-xs font-bold text-[#3f3f46]">
                                {row.bucketLabel}
                            </td>
                            {columnHeaders.map((header) => {
                                const cell = row.cells[header];
                                return (
                                    <td
                                        key={`${row.bucketLabel}-${header}`}
                                        className="w-[225px] min-w-[225px] max-w-[225px] border border-[#e4e4e7] p-0 align-top h-[220px]"
                                    >
                                        {cell != null ? renderCell(cell as NonNullable<TCell>, row.bucketLabel, header) : (emptyCellNode ?? DEFAULT_EMPTY_CELL_NODE)}
                                    </td>
                                );
                            })}
                            {fillerWidths.map((w, i) => (
                                <td
                                    key={`filler-${i}`}
                                    className="p-0"
                                    style={{ width: w, minWidth: w, maxWidth: w }}
                                />
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default GenericWhitespaceGrid;
